import { OverlayContainer } from '@angular/cdk/overlay';
import { ElementRef, inject, Injectable, OnDestroy, Renderer2 } from '@angular/core';

import { ApActiveModal, ApModalHelperBase } from 'webcommon/core-ui/modals-ng1520';


// This class contains common logic that needs to be run inside of ngx-bootstrap modals.
// It previously existed in class ApBaseModalComponent, but was moved here to allow it to be shared
// with ApLockingBaseModalComponent (previously FndBaseModalComponent), etc.
// This class is not actually injectable because FakeHideHandlerInjectionToken doesn't have a service registered for it.
// The decorator is required because it relies on the OnDestroy lifecycle hook and Angular requires a decorator to implement the hook.
@Injectable()
export class ApModalHelper<T> extends ApModalHelperBase<T> implements OnDestroy {
  private activeModal = inject<ApActiveModal<T>>(ApActiveModal);
  private renderer2 = inject(Renderer2);
  private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private overlayContainer = inject(OverlayContainer);

  constructor(
  ) {
    super();
    this.activeModal.markReadyToHide();
  }

  private makeCompatibleWithBootstrapModalOnDestroy() {
    // revert changes that were made outside of this modal element and its wrapper
    this.overlayContainer.getContainerElement().classList.remove('modal-container-top');
  }

  // call from ngOnInit in component
  onInit(): void {
    this.makeCompatibleWithBootstrapModalOnInit({
      renderer2: this.renderer2,
      elementRef: this.elementRef,
    });
  }

  modalData<TData>(): TData {
    // the same value as inject(DIALOG_DATA)
    // https://github.com/angular/components/blob/20.2.14/src/cdk/dialog/dialog.ts#L333-L336
    return this.activeModal.dialogRef.config.data as TData;
  }

  // resolve the promise because the user is done and there is a successful result
  emitAndClose(result: T | PromiseLike<T>): void {
    this.activeModal.emitAndClose(result);
  }
  // reject the promise
  dismiss(reason?: unknown): void {
    this.activeModal.dismiss(reason);
  }

  // resolve the promise because there is a successful result, but don't close the modal
  // call when closing of modal is already done elsewhere
  // THIS SHOULD ONLY BE CALLED IN VERY RARE CASES
  emitWithoutClose(result: T | PromiseLike<T>): void {
    this.activeModal.emitWithoutClose(result);
  }
  // reject the promise, but don't close the modal
  // call when closing of modal is already done elsewhere
  // THIS SHOULD ONLY BE CALLED IN VERY RARE CASES
  dismissWithoutClose(reason?: unknown): void {
    this.activeModal.dismissWithoutClose(reason);
  }

  override ngOnDestroy(): void {
    this.makeCompatibleWithBootstrapModalOnDestroy();
  }
}
